"use client";

import {
  BarChart3,
  BarChart4,
  ChartCandlestick,
  ChartArea,
  ChartColumnStacked,
  ChartGantt,
  CircleDot,
  Gauge,
  Grid3x3,
  LineChart,
  PieChart,
  Radar,
  StepForward,
  Table2,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils/cn";

import {
  GRAPH_TYPE_OPTIONS,
  DEFERRED_GRAPH_TYPE_OPTIONS,
  LIVE_READY_GRAPH_TYPE_OPTIONS,
  type GraphType,
} from "../../schema/datalogger.schema";

const GRAPH_ICONS: Record<GraphType, LucideIcon> = {
  line: LineChart,
  bar: BarChart3,
  area: ChartArea,
  scatter: CircleDot,
  gauge: Gauge,
  step: StepForward,
  histogram: BarChart4,
  stacked_bar: ChartColumnStacked,
  donut: PieChart,
  heatmap: Grid3x3,
  radar: Radar,
  candlestick: ChartCandlestick,
  ohlc: ChartCandlestick,
  gantt: ChartGantt,
  table: Table2,
};

type GraphTypeSelectorProps = {
  value: GraphType;
  onChange: (g: GraphType) => void;
  className?: string;
  options?: readonly GraphType[];
};

/**
 * Icon-first chart type picker (Stripe / Linear style).
 */
export function GraphTypeSelector({ value, onChange, className, options }: GraphTypeSelectorProps) {
  const allowed = options ? LIVE_READY_GRAPH_TYPE_OPTIONS.filter((option) => options.includes(option.value)) : LIVE_READY_GRAPH_TYPE_OPTIONS;
  const deferredAllowed = options
    ? DEFERRED_GRAPH_TYPE_OPTIONS.filter((option) => options.includes(option.value))
    : DEFERRED_GRAPH_TYPE_OPTIONS;
  const activeLabel =
    allowed.find((o) => o.value === value) ??
    deferredAllowed.find((o) => o.value === value) ??
    GRAPH_TYPE_OPTIONS.find((o) => o.value === value);
  return (
    <div className={cn("space-y-ds-2", className)}>
      <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Visualization</p>
      <div className="flex flex-wrap gap-ds-2">
        {allowed.map((opt) => {
          const Icon = GRAPH_ICONS[opt.value];
          const on = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              title={opt.label}
              aria-label={opt.label}
              aria-pressed={on}
              onClick={() => onChange(opt.value)}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-control transition-all duration-200 ease-out",
                "ring-1 ring-transparent hover:bg-muted-bg/50 hover:ring-border/60",
                on
                  ? "bg-primary/10 text-primary shadow-card ring-primary/20"
                  : "bg-card text-muted hover:text-foreground dark:bg-muted-bg/20",
              )}
            >
              <Icon className="h-[1.125rem] w-[1.125rem]" aria-hidden />
            </button>
          );
        })}
      </div>
      <p className="text-caption text-muted">
        {activeLabel?.label ?? value}
      </p>
      {deferredAllowed.length > 0 ? (
        <p className="text-[0.72rem] text-muted/85">
          Dashboard-only for now: {deferredAllowed.map((option) => option.label).join(", ")}
        </p>
      ) : null}
    </div>
  );
}
